import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import api from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function DemoLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();

  const DEMO_USERS = [
    { label: t("demo.roles.admin_label"), desc: t("demo.roles.admin_desc"), email: "admin@vinculo.edu", password: "admin123", testid: "quick-admin" },
    { label: t("demo.roles.facultad_label"), desc: t("demo.roles.facultad_desc"), email: "decano.ing@vinculo.edu", password: "facultad123", testid: "quick-faculty" },
    { label: t("demo.roles.unidad_label"), desc: t("demo.roles.unidad_desc"), email: "jefe.informatica@vinculo.edu", password: "unidad123", testid: "quick-unidad" },
    { label: t("demo.roles.directivo_label"), desc: t("demo.roles.directivo_desc"), email: "rector@vinculo.edu", password: "directivo123", testid: "quick-director" },
  ];

  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    api.post("/seed/demo").catch(() => {});
  }, []);

  const quickLogin = async (email, password, testid) => {
    setLoading(testid);
    try {
      await login(email, password);
      toast.success(t("common.welcome"));
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="brand-home">
            <span className="text-2xl font-semibold tracking-tighter">vincularedu</span>
            <span className="text-2xl font-medium text-zinc-400">.</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1.5" data-testid="back-home">
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("nav.volver_inicio")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 sm:mb-16">
            <div className="section-marker text-amber-700 mb-4 inline-flex items-center gap-2">
              <Sparkles size={12} strokeWidth={2} /> {t("demo.kicker")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]">
              {t("demo.h1_a")} <span className="serif-em">{t("demo.h1_em")}</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
              {t("demo.desc")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200" data-testid="demo-cards">
            {DEMO_USERS.map((u) => (
              <button
                key={u.email}
                onClick={() => quickLogin(u.email, u.password, u.testid)}
                disabled={loading !== null}
                className="bg-white p-6 sm:p-8 text-left hover:bg-zinc-50 transition-colors disabled:opacity-50 group flex flex-col"
                data-testid={u.testid}
              >
                <div className="section-marker mb-3">{t("demo.enter_as")}</div>
                <div className="text-xl sm:text-2xl font-medium tracking-tight mb-2">{u.label}</div>
                <p className="text-sm text-zinc-600 mb-6 flex-1">{u.desc}</p>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span className="font-mono">{u.email}</span>
                  <span className="inline-flex items-center gap-1 text-zinc-950 group-hover:gap-2 transition-all">
                    {loading === u.testid ? t("demo.entering") : t("demo.enter")}
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-12 border border-zinc-200 bg-zinc-50 p-6">
            <div className="section-marker mb-2">⚠ {t("demo.warn_title")}</div>
            <p className="text-sm text-zinc-600 leading-relaxed">
              {t("demo.warn_text")}{" "}
              <Link to="/login" className="underline text-zinc-950" data-testid="link-real-login">{t("demo.warn_link")}</Link>{" "}
              {t("demo.warn_or")}{" "}
              <a href="mailto:contacto@cdesign.cl" className="underline text-zinc-950">{t("demo.warn_contact")}</a>.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
